import { computed, type ComputedRef } from 'vue'
import type { ModalProps } from '@nuxt/ui'
import type { FocusOutsideEvent, PointerDownOutsideEvent } from 'reka-ui'
import { useOverlayFocusRestore } from '~/composables/useOverlayFocusRestore'
import type { OverlayShellEvents } from '~/types/overlays/OverlayShellEvents'
import { TOAST_REGION_SELECTOR } from '~/utils/toastRegion.constants'

type OverlayShellEmit = <K extends keyof OverlayShellEvents>(
  event: K,
  ...args: OverlayShellEvents[K]
) => void

type OverlayContent = NonNullable<ModalProps['content']>

interface OverlayShellOptions {
  isOpen: () => boolean
  closeOnOutsideClick: () => boolean
  /** Whether the overlay blocks the page behind it; defaults to true. */
  isModal?: () => boolean
  emit: OverlayShellEmit
}

interface OverlayShell {
  onOpenChange: (open: boolean) => void
  onAfterLeave: () => void
  contentProps: ComputedRef<OverlayContent>
}

/** Dismissal, focus return and the outside-click opt-out shared by every dialog and drawer shell. */
export const useOverlayShell = (options: OverlayShellOptions): OverlayShell => {
  const { restore } = useOverlayFocusRestore(options.isOpen)

  const onOpenChange = (open: boolean): void => {
    if (open) return
    options.emit('update:modelValue', false)
    options.emit('close')
  }

  const onAfterLeave = (): void => {
    restore()
    options.emit('afterLeave')
  }

  // Blocks outside interactions only; Escape still dismisses. Toasts never dismiss an overlay.
  const onInteractOutside = (event: PointerDownOutsideEvent | FocusOutsideEvent): void => {
    const target = event.detail.originalEvent.target
    if (target instanceof Element && target.closest(TOAST_REGION_SELECTOR) !== null) {
      event.preventDefault()
      return
    }
    if (options.closeOnOutsideClick()) return
    event.preventDefault()
  }

  const contentProps = computed<OverlayContent>(() => ({
    ...(options.isModal?.() === false ? {} : { 'aria-modal': 'true' }),
    onInteractOutside
  }))

  return { onOpenChange, onAfterLeave, contentProps }
}
